import { useEffect, useState } from 'react';
import { Filter, Download, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  PageHeader,
  DataTable,
  Column,
  StatusBadge,
} from '@/components/common';
import { auditLogsApi, AuditLog, QueryAuditLogParams } from '@/api/services';
import { getErrorMessage } from '@/api/client';

export default function AuditLogs() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page] = useState(1);
  const [resultFilter, setResultFilter] = useState<string>('all');
  const [actionFilter, setActionFilter] = useState<string>('all');

  const fetchLogs = async (params?: QueryAuditLogParams) => {
    try {
      setLoading(true);
      const response = await auditLogsApi.getAll({
        ...params,
        page,
        limit: 20,
      });
      setLogs(response.data);
      setTotal(response.meta.total);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params: QueryAuditLogParams = {};
    if (resultFilter !== 'all') params.result = resultFilter as 'ALLOW' | 'DENY';
    if (actionFilter !== 'all') params.action = actionFilter;
    fetchLogs(params);
  }, [page, resultFilter, actionFilter]);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const columns: Column<AuditLog>[] = [
    {
      key: 'timestamp',
      header: 'Time',
      sortable: true,
      render: (log) => (
        <span className="text-muted-foreground font-mono text-sm">
          {formatTimestamp(log.timestamp)}
        </span>
      ),
    },
    {
      key: 'user',
      header: 'User',
      render: (log) => (
        <div>
          <div className="font-medium">{log.user.fullName}</div>
          <div className="text-sm text-muted-foreground">{log.user.email}</div>
        </div>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      render: (log) => (
        <Badge
          variant="outline"
          className={
            log.action === 'GET'
              ? 'border-blue-200 text-blue-700 dark:border-blue-800 dark:text-blue-400'
              : log.action === 'POST'
              ? 'border-emerald-200 text-emerald-700 dark:border-emerald-800 dark:text-emerald-400'
              : log.action === 'PUT' || log.action === 'PATCH'
              ? 'border-amber-200 text-amber-700 dark:border-amber-800 dark:text-amber-400'
              : 'border-red-200 text-red-700 dark:border-red-800 dark:text-red-400'
          }
        >
          {log.action}
        </Badge>
      ),
    },
    {
      key: 'resource',
      header: 'Resource',
      render: (log) => (
        <div className="max-w-xs truncate font-mono text-sm text-muted-foreground">
          {log.resource}
        </div>
      ),
    },
    {
      key: 'accessCode',
      header: 'Permission',
      render: (log) => (
        <Badge variant="secondary" className="font-mono text-xs">
          {log.accessCode}
        </Badge>
      ),
    },
    {
      key: 'result',
      header: 'Result',
      render: (log) => (
        <StatusBadge
          status={log.result === 'ALLOW' ? 'approved' : 'rejected'}
          label={log.result}
        />
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: () => (
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <Eye className="h-4 w-4" />
        </Button>
      ),
    },
  ];

  return (
    <div className="p-6">
      <PageHeader
        title="Audit Logs"
        description="Track all user actions and system events"
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: 'Audit Logs' },
        ]}
        actions={
          <Button variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        }
      />

      <div className="mb-4 flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={resultFilter} onValueChange={setResultFilter}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Result" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Results</SelectItem>
              <SelectItem value="ALLOW">Allow</SelectItem>
              <SelectItem value="DENY">Deny</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            <SelectItem value="GET">GET</SelectItem>
            <SelectItem value="POST">POST</SelectItem>
            <SelectItem value="PUT">PUT</SelectItem>
            <SelectItem value="PATCH">PATCH</SelectItem>
            <SelectItem value="DELETE">DELETE</SelectItem>
          </SelectContent>
        </Select>

        <div className="ml-auto text-sm text-muted-foreground">
          {total} total records
        </div>
      </div>

      <DataTable
        data={logs}
        columns={columns}
        loading={loading}
        emptyTitle="No audit logs found"
        emptyDescription="Audit logs will appear here as users interact with the system."
        emptyAction={null}
      />
    </div>
  );
}
